'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis } from 'recharts';

const MODELS = [
  { id: 'random_forest', name: 'Random Forest', version: '2.2.0', rank: 1, accuracy: 74.8, brier: 0.1845, logLoss: 0.4521, calibration: 0.9234, liveAccuracy: 76.2, predictions: 15678, status: 'deployed', color: '#00D4FF', trend: '+0.5%' },
  { id: 'xgboost', name: 'XGBoost', version: '1.9.0', rank: 2, accuracy: 72.9, brier: 0.1967, logLoss: 0.4712, calibration: 0.9087, liveAccuracy: 74.1, predictions: 15678, status: 'deployed', color: '#FF6B35', trend: '+0.2%' },
  { id: 'lightgbm', name: 'LightGBM', version: '1.6.0', rank: 3, accuracy: 71.8, brier: 0.2012, logLoss: 0.4834, calibration: 0.8976, liveAccuracy: 72.8, predictions: 15678, status: 'deployed', color: '#00C853', trend: '+0.4%' },
  { id: 'elo', name: 'Elo Dynamique', version: '3.1.0', rank: 4, accuracy: 70.1, brier: 0.2145, logLoss: 0.5089, calibration: 0.8712, liveAccuracy: 71.3, predictions: 15678, status: 'deployed', color: '#FFB800', trend: '+0.6%' },
  { id: 'logistic_regression', name: 'Logistic Regression', version: '1.3.0', rank: 5, accuracy: 68.9, brier: 0.2198, logLoss: 0.5201, calibration: 0.8634, liveAccuracy: 69.7, predictions: 15678, status: 'deployed', color: '#A855F7', trend: '+0.1%' },
];

const TRAINING_HISTORY = [
  { date: 'Nov', rf: 72.1, xgb: 70.2, lgbm: 69.1, elo: 67.8, lr: 66.9 },
  { date: 'Dec', rf: 72.8, xgb: 70.9, lgbm: 69.7, elo: 68.2, lr: 67.4 },
  { date: 'Jan', rf: 73.4, xgb: 71.5, lgbm: 70.3, elo: 68.9, lr: 67.8 },
  { date: 'Feb', rf: 74.1, xgb: 72.1, lgbm: 71.0, elo: 69.5, lr: 68.3 },
  { date: 'Mar', rf: 74.5, xgb: 72.6, lgbm: 71.5, elo: 69.9, lr: 68.7 },
  { date: 'May', rf: 74.8, xgb: 72.9, lgbm: 71.8, elo: 70.1, lr: 68.9 },
];

const RADAR_DATA = [
  { metric: 'Précision', rf: 95, xgb: 92, lgbm: 91 },
  { metric: 'Calibration', rf: 92, xgb: 89, lgbm: 88 },
  { metric: 'Brier Score', rf: 94, xgb: 90, lgbm: 88 },
  { metric: 'Log Loss', rf: 93, xgb: 89, lgbm: 87 },
  { metric: 'Live Acc.', rf: 96, xgb: 93, lgbm: 91 },
  { metric: 'Stabilité', rf: 91, xgb: 87, lgbm: 86 },
];

const TOOLTIP_STYLE = { backgroundColor: '#111827', border: '1px solid #1E2D4A', borderRadius: '6px', color: '#E8EDF5', fontSize: '11px' };

export default function AutoTrainingClient() {
  const [training, setTraining] = useState(false);
  const [trainingProgress, setTrainingProgress] = useState(0);
  const [selectedModel, setSelectedModel] = useState('random_forest');
  const [lastTrained] = useState('Aujourd\'hui 04:00 UTC');

  const handleTrain = () => {
    setTraining(true);
    setTrainingProgress(0);
    const interval = setInterval(() => {
      setTrainingProgress((p) => {
        if (p >= 100) { clearInterval(interval); setTraining(false); return 100; }
        return p + Math.random() * 8;
      });
    }, 200);
  };

  const activeModel = MODELS?.find((m) => m?.id === selectedModel) || MODELS?.[0];

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">AutoTraining Engine</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Réentraînement automatique quotidien · Dernier: {lastTrained}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-muted-foreground">Prochain entraînement: <span className="text-primary font-mono-data">04:00 UTC</span></div>
          <button onClick={handleTrain} disabled={training} className="btn-primary flex items-center gap-2 text-sm px-4 py-2">
            <Icon name="CpuChipIcon" size={16} className={training ? 'animate-pulse' : ''} />
            {training ? `Entraînement... ${Math.min(100, Math.round(trainingProgress))}%` : 'Lancer Entraînement'}
          </button>
        </div>
      </div>
      {/* Training Progress */}
      {training && (
        <div className="card p-4 border-primary/30">
          <div className="flex items-center gap-3 mb-3">
            <Icon name="CpuChipIcon" size={18} className="text-primary animate-pulse" />
            <span className="text-sm font-medium text-foreground">Entraînement en cours — 5 modèles</span>
            <span className="ml-auto text-sm font-mono-data text-primary">{Math.min(100, Math.round(trainingProgress))}%</span>
          </div>
          <div className="h-2 bg-card-hover rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary to-positive rounded-full transition-all duration-300" style={{ width: `${Math.min(100, trainingProgress)}%` }} />
          </div>
          <div className="flex gap-4 mt-3 text-2xs text-muted-foreground">
            <span>✓ Chargement données</span>
            <span className={trainingProgress > 30 ? 'text-positive' : ''}>✓ Feature engineering</span>
            <span className={trainingProgress > 60 ? 'text-positive' : 'text-muted-foreground'}>⟳ Optimisation hyperparamètres</span>
            <span className={trainingProgress > 85 ? 'text-positive' : 'text-muted-foreground'}>⟳ Validation croisée</span>
          </div>
        </div>
      )}
      {/* Model Leaderboard */}
      <div className="card p-4">
        <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="TrophyIcon" size={16} className="text-warning" />
          Leaderboard des Modèles
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                {['Rang', 'Modèle', 'Version', 'Précision', 'Brier Score', 'Log Loss', 'Calibration', 'Live Acc.', 'Tendance', 'Statut']?.map((h) => (
                  <th key={h} className="text-left py-2 px-3 text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MODELS?.map((model) => (
                <tr
                  key={model?.id}
                  onClick={() => setSelectedModel(model?.id)}
                  className={`border-b border-border/50 cursor-pointer transition-colors ${selectedModel === model?.id ? 'bg-primary/5' : 'hover:bg-card-hover'}`}
                >
                  <td className="py-2 px-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${model?.rank === 1 ? 'bg-warning/20 text-warning' : 'bg-card-hover text-muted-foreground'}`}>
                      {model?.rank === 1 ? '🥇' : model?.rank === 2 ? '🥈' : model?.rank === 3 ? '🥉' : model?.rank}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: model?.color }} />
                      <span className="font-medium text-foreground">{model?.name}</span>
                      {model?.rank === 1 && <span className="status-badge bg-positive-subtle text-positive text-2xs">BEST</span>}
                    </div>
                  </td>
                  <td className="py-2 px-3 font-mono-data text-muted-foreground">v{model?.version}</td>
                  <td className="py-2 px-3 font-mono-data font-semibold" style={{ color: model?.color }}>{model?.accuracy}%</td>
                  <td className="py-2 px-3 font-mono-data text-foreground">{model?.brier}</td>
                  <td className="py-2 px-3 font-mono-data text-foreground">{model?.logLoss}</td>
                  <td className="py-2 px-3 font-mono-data text-foreground">{model?.calibration}</td>
                  <td className="py-2 px-3 font-mono-data text-positive">{model?.liveAccuracy}%</td>
                  <td className="py-2 px-3 font-mono-data text-positive">{model?.trend}</td>
                  <td className="py-2 px-3">
                    <span className="status-badge bg-positive-subtle text-positive">deployed</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Évolution Précision (6 mois)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={TRAINING_HISTORY}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" />
              <XAxis dataKey="date" tick={{ fill: '#6B7A99', fontSize: 10 }} />
              <YAxis domain={[65, 77]} tick={{ fill: '#6B7A99', fontSize: 10 }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              {MODELS?.map((m) => (
                <Line key={m?.id} type="monotone" dataKey={m?.id === 'random_forest' ? 'rf' : m?.id === 'xgboost' ? 'xgb' : m?.id === 'lightgbm' ? 'lgbm' : m?.id === 'elo' ? 'elo' : 'lr'} stroke={m?.color} strokeWidth={m?.rank === 1 ? 2.5 : 1.5} dot={false} name={m?.name} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Comparaison Radar — Top 3</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={RADAR_DATA}>
              <PolarGrid stroke="#1E2D4A" />
              <PolarAngleAxis dataKey="metric" tick={{ fill: '#6B7A99', fontSize: 10 }} />
              <Radar name="Random Forest" dataKey="rf" stroke="#00D4FF" fill="#00D4FF" fillOpacity={0.15} strokeWidth={2} />
              <Radar name="XGBoost" dataKey="xgb" stroke="#FF6B35" fill="#FF6B35" fillOpacity={0.1} strokeWidth={1.5} />
              <Radar name="LightGBM" dataKey="lgbm" stroke="#00C853" fill="#00C853" fillOpacity={0.1} strokeWidth={1.5} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Model Detail */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-foreground mb-4">Détail — {activeModel?.name} v{activeModel?.version}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Précision Globale', value: `${activeModel?.accuracy}%`, color: 'text-primary' },
            { label: 'Brier Score', value: activeModel?.brier?.toString(), color: 'text-positive' },
            { label: 'Log Loss', value: activeModel?.logLoss?.toString(), color: 'text-foreground' },
            { label: 'Calibration', value: activeModel?.calibration?.toString(), color: 'text-positive' },
            { label: 'Précision Live', value: `${activeModel?.liveAccuracy}%`, color: 'text-primary' },
            { label: 'Total Prédictions', value: activeModel?.predictions?.toLocaleString(), color: 'text-foreground' },
            { label: 'Tendance', value: activeModel?.trend, color: 'text-positive' },
            { label: 'Statut', value: 'Déployé', color: 'text-positive' },
          ]?.map((item) => (
            <div key={item?.label} className="p-3 rounded-lg bg-card-hover">
              <div className="text-2xs text-muted-foreground mb-1">{item?.label}</div>
              <div className={`text-lg font-bold font-mono-data ${item?.color}`}>{item?.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
